import networkService from '../Network/NetworkManager';
const APPURL = require('../Constants/URLConstants');

/**
 * Common utility for uploading images to S3
 * Supports multiple input formats and single/multiple file uploads
 * Includes image compression and WebP conversion for web
 *
 * @param {Object} options - Upload options
 * @param {Object|Array} options.image_file - Single image object or array of images
 * @param {Function} [options.onProgress] - Callback for progress updates (for multiple files)
 * @param {Function} [options.onError] - Custom error handler
 * @param {Object} [options.compression] - Compression options
 * @param {number} [options.compression.maxWidth=1920] - Max width in pixels
 * @param {number} [options.compression.maxHeight=1920] - Max height in pixels
 * @param {number} [options.compression.quality=0.85] - Quality (0-1)
 * @param {boolean} [options.compression.convertToWebP=true] - Convert to WebP format
 * @returns {Promise<string|string[]> - File URI(s) or null on failure
 */
export const uploadImageAPI = async ({
  image_file,
  onProgress,
  onError,
  compression = {
    maxWidth: 1920,
    maxHeight: 1920,
    quality: 0.85,
    convertToWebP: true,
  }
}) => {
  try {
    // Handle multiple images (array)
    if (Array.isArray(image_file)) {
      const validImages = image_file.filter(img => img != null);

      if (validImages.length === 0) {
        return null;
      }

      // Compress images before upload
      let processedImages = validImages;
      if (typeof window !== 'undefined' && compression) {
        try {
          processedImages = await Promise.all(
            validImages.map(img => compressImageIfNeeded(img, compression))
          );
        } catch (compressError) {
          console.warn('Image compression failed, using original:', compressError);
          processedImages = validImages;
        }
      }

      // Prepare file parameters for all images
      const imgParms = processedImages.map(img => ({
        name: extractFileName(img).replace(/ /g, ''),
        type: extractFileType(img),
      }));

      // Get signed URLs for all files
      const responseJson = await networkService.networkCall(
        APPURL.URLPaths.S3signedUploadURL,
        'POST',
        JSON.stringify({files: imgParms}),
      );

      if (responseJson?.status === true) {
        const results = responseJson?.data?.result || [];
        let completedUploads = 0;

        // Upload all files in parallel
        const uploadPromises = processedImages.map(async (img, index) => {
          try {
            const base64Url = extractBase64Url(img);
            const blob = await fetch(base64Url).then(res => res.blob());

            await networkService.uploadFileWithSignedURL(
              results[index]?.signedUrl,
              imgParms[index]?.type,
              blob,
            );

            completedUploads++;
            if (onProgress) {
              onProgress(completedUploads, processedImages.length);
            }

            return results[index]?.fileUri || null;
          } catch (error) {
            console.error(`File upload failed for image ${index}:`, error);
            return null;
          }
        });

        const uriResults = await Promise.all(uploadPromises);
        return uriResults.filter(uri => uri != null);
      } else {
        if (onError) {
          onError(responseJson);
        }
        return null;
      }
    }

    // Handle single image
    if (!image_file) {
      return null;
    }

    // Compress single image before upload
    let processedImage = image_file;
    if (typeof window !== 'undefined' && compression) {
      try {
        processedImage = await compressImageIfNeeded(image_file, compression);
      } catch (compressError) {
        console.warn('Image compression failed, using original:', compressError);
        processedImage = image_file;
      }
    }

    const dict = {
      name: extractFileName(processedImage).replace(/ /g, ''),
      type: extractFileType(processedImage),
    };

    const responseJson = await networkService.networkCall(
      APPURL.URLPaths.S3signedUploadURL,
      'POST',
      JSON.stringify({files: [dict]}),
    );

    if (responseJson?.status === true) {
      const result = responseJson?.data?.result?.[0];
      if (!result) {
        return null;
      }

      const base64Url = extractBase64Url(processedImage);
      const blob = await fetch(base64Url).then(res => res.blob());

      await networkService.uploadFileWithSignedURL(
        result.signedUrl,
        dict.type,
        blob,
      );

      return result.fileUri;
    } else {
      if (onError) {
        onError(responseJson);
      }
      return null;
    }
  } catch (error) {
    console.error('uploadImageAPI error:', error);
    if (onError) {
      onError(error);
    }
    return null;
  }
};

/**
 * Compress image if needed (web only)
 * Converts to WebP and resizes if larger than max dimensions
 *
 * @param {Object} image - Image object with base64 data
 * @param {Object} options - Compression options
 * @returns {Promise<Object>} - Processed image object
 */
export async function compressImageIfNeeded(image, options) {
  const {maxWidth = 1920, maxHeight = 1920, quality = 0.85, convertToWebP = true} = options;

  // Skip SVG images to avoid degradation
  const mimeType = extractFileType(image);

  if (mimeType === 'image/svg+xml' || mimeType === 'image/svg' || mimeType.includes('svg')) {
    return image;
  }

  const base64Url = extractBase64Url(image);

  return new Promise((resolve) => {
    const img = new Image();

    img.onload = () => {
      let {width, height} = img;

      // Check if resizing is needed
      const needsResize = width > maxWidth || height > maxHeight;

      const scale = Math.min(maxWidth / width, maxHeight / height, 1);
      const targetWidth = Math.round(width * scale);
      const targetHeight = Math.round(height * scale);

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('Failed to get canvas context');
        resolve(image);
        return;
      }

      // Use better quality scaling
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      // Determine target format
      const isRaster =
        mimeType.includes('jpeg') ||
        mimeType.includes('jpg') ||
        mimeType.includes('png') ||
        mimeType.includes('webp') ||
        mimeType.includes('heic') ||
        mimeType.includes('heif');

      const targetType = convertToWebP && isRaster ? 'image/webp' : mimeType;

      // Check browser support for WebP
      const canvasTest = document.createElement('canvas');
      const webPSupported = canvasTest.toDataURL('image/webp').indexOf('data:image/webp') === 0;

      if (targetType === 'image/webp' && !webPSupported) {
        console.warn('WebP not supported, falling back to original format');
        resolve(image);
        return;
      }

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            console.error('Failed to create blob from canvas');
            resolve(image);
            return;
          }

          // Convert blob back to base64
          const reader = new FileReader();
          reader.onload = () => {
            const compressedBase64 = reader.result;

            // Create new image object with compressed data
            const compressedImage = {...image};

            if (image.base64) {
              compressedImage.base64 = compressedBase64;
            }
            if (image.image) {
              compressedImage.image = compressedBase64;
            }

            // Update type if converted to WebP
            if (targetType === 'image/webp') {
              compressedImage.type = 'image/webp';

              // Get original filename from various possible locations
              let originalName = image.name;
              if (!originalName && image.fileList && image.fileList[0] && image.fileList[0].name) {
                originalName = image.fileList[0].name;
              }

              // Update name property (FileList is read-only, so we don't modify it)
              if (originalName) {
                const dotIndex = originalName.lastIndexOf('.');
                const webpName = dotIndex > 0
                  ? `${originalName.slice(0, dotIndex)}.webp`
                  : `${originalName}.webp`;
                compressedImage.name = webpName;
              }
            }

            resolve(compressedImage);
          };
          reader.onerror = (error) => {
            console.error('FileReader error:', error);
            resolve(image);
          };
          reader.readAsDataURL(blob);
        },
        targetType,
        quality
      );
    };

    img.onerror = (error) => {
      console.error('Failed to load image for compression:', error);
      resolve(image);
    };

    // Set crossOrigin for base64 images to avoid CORS issues with canvas
    if (base64Url.startsWith('data:')) {
      img.crossOrigin = 'anonymous';
    }
    img.src = base64Url;
  });
}

/**
 * Extract file name from various image object structures
 * Handles different formats used across the codebase
 * NOTE: Checks 'name' property FIRST to allow WebP conversion overrides
 */
function extractFileName(image) {
  // Format: {name: ...} - Check this first for WebP converted names
  if (image?.name) {
    return image.name;
  }
  // Format: {fileList: [{name: ...}]}
  if (image?.fileList?.[0]?.name) {
    return image.fileList[0].name;
  }
  // Format: {base64: ...} - generate default name
  if (image?.base64) {
    const timeStamp = new Date().getTime();
    const randomSuffix = Math.floor(Math.random() * 10000);
    const type = image?.type || 'jpeg';
    return `image_${timeStamp}_${randomSuffix}.${type}`;
  }
  return 'image_default.jpg';
}

/**
 * Extract file type from various image object structures
 * NOTE: Checks 'type' property FIRST to allow WebP conversion overrides
 */
function extractFileType(image) {
  // Format: {type: ...} - Check this first for WebP converted types
  if (image?.type) {
    return image.type;
  }
  // Format: {fileList: [{type: ...}]}
  if (image?.fileList?.[0]?.type) {
    return image.fileList[0].type;
  }
  return 'image/jpeg';
}

/**
 * Extract base64 URL from various image object structures
 */
function extractBase64Url(image) {
  // Format: {base64: ...}
  if (image?.base64) {
    return image.base64;
  }
  // Format: {image: ...} (used in AddListings)
  if (image?.image) {
    return image.image;
  }
  throw new Error('Invalid image format: no base64 data found');
}
